import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, FileText, ClipboardList, Save, AlertTriangle, CheckCircle2, ArrowRight, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { labelContexte, isRestructuration } from "@/lib/checklist";
import { fetchPortail, fetchPortailQuestionnaire, savePortailQuestionnaire, uploadPortailDocument, extractIdentitePortail, extractBanquesPortail, fetchEtablissementsPortail, extractCapitalPortail, extractDateJugementPortail, dateJugementStatutPortail, uploadPortailSignedDocument, portailLivrableDownloadUrl } from "@/lib/portail";
import { countReponses } from "@/lib/questionnaire";
import QuestionnaireForm from "@/components/questionnaire/QuestionnaireForm";
import PortailDataroom from "@/components/dataroom/PortailDataroom";
import LivrablesSection from "@/components/dataroom/LivrablesSection";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function Header({ info }) {
    return (
        <header className="bg-primary text-primary-foreground">
            <div className="mx-auto max-w-5xl px-6 py-8 flex flex-wrap items-end justify-between gap-6">
                <img src="/logos/logo-acs-blanc.webp" alt="ACS Corporate" className="h-10 w-auto -ml-2" />
                {info && (
                    <div className="text-right">
                        <p className="text-xs uppercase tracking-[0.22em] text-white/50">Espace client sécurisé</p>
                        <p data-testid="portail-mission" className="mt-1 font-display text-xl">{info.nom_mission || "Mission"}</p>
                        <p data-testid="portail-entreprise" className="text-sm text-white/70">{info.entreprise} · SIREN {info.siren}</p>
                    </div>
                )}
            </div>
        </header>
    );
}

export default function PortailClient() {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const isCombined = searchParams.get("flow") === "combined";
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [email, setEmail] = useState(sessionStorage.getItem(`portail-email-${token}`) || "");
    const [unlocked, setUnlocked] = useState(false);
    const [reponses, setReponses] = useState({});
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [etablissements, setEtablissements] = useState([]);

    const PARTNERS = ["notaire", "commissaire_comptes", "avocat"];
    const PARTNER_LABELS = { notaire: "Notaire", commissaire_comptes: "Commissaire aux comptes", avocat: "Avocat" };
    function requireCerfa() {
        const missing = PARTNERS.filter((p) => !String(reponses?.juridique?.[p] || "").trim());
        if (missing.length) {
            toast.error("Partenaires légaux obligatoires", { description: `Renseignez (ou cochez « N/A ») : ${missing.map((p) => PARTNER_LABELS[p]).join(", ")}.` });
            return false;
        }
        return true;
    }

    useEffect(() => {
        let cancelled = false;
        fetchPortail(token).then((i) => { if (!cancelled) setInfo(i); }).catch((e) => { if (!cancelled) setError(e.message); });
        return () => { cancelled = true; };
    }, [token]);

    async function unlock(e) {
        e.preventDefault();
        const v = email.trim().toLowerCase();
        if (!EMAIL_RE.test(v)) return toast.error("Adresse e-mail invalide");
        try {
            const q = await fetchPortailQuestionnaire(token, v);
            setReponses(q.reponses || {});
            setEmail(v);
            sessionStorage.setItem(`portail-email-${token}`, v);
            setUnlocked(true);
            fetchEtablissementsPortail(token, v).then((r) => setEtablissements(r?.etablissements || [])).catch(() => {});
        } catch (err) {
            toast.error(err.message);
        }
    }

    async function save() {
        // HOTFIX UX — la sauvegarde brouillon ne bloque JAMAIS : enregistrement partiel à 100 % autorisé.
        setSaving(true);
        try {
            await savePortailQuestionnaire(token, email, reponses);
            setDirty(false);
            toast.success("Réponses enregistrées", { description: `Contributeur : ${email}` });
        } catch (e) {
            toast.error("Enregistrement impossible", { description: e.message });
        } finally {
            setSaving(false);
        }
    }

    async function submit() {
        // La soumission FINALE reste bloquante (dossier complet exigé).
        if (!reponses?.contexte_demande) return toast.error("Champ obligatoire", { description: "Sélectionnez le contexte de votre demande en haut du questionnaire." });
        if (!requireCerfa()) return;
        setSaving(true);
        try {
            await savePortailQuestionnaire(token, email, reponses);
            setDirty(false);
            if (isCombined) {
                toast.success("Questionnaire transmis", { description: "Passons au prévisionnel financier." });
                navigate(`/client/${token}/previsionnel`);
                return;
            }
            setSubmitted(true);
        } catch (e) {
            toast.error("Envoi impossible", { description: e.message });
        } finally {
            setSaving(false);
        }
    }

    async function upload(files, typeDocument, periode, typePersonnalise, estHolding) {
        for (const file of files) {
            try {
                const doc = await uploadPortailDocument(token, { file, email, typeDocument, periode, typePersonnalise, estHolding });
                setInfo((i) => ({ ...i, documents: [doc, ...(i.documents || [])] }));
                toast.success("Document transmis", { description: file.name });
                if (typeDocument === "pv_jugement") pollDateJugement(doc.id);
            } catch (e) {
                toast.error(`Échec : ${file.name}`, { description: e.message });
            }
        }
    }

    async function pollDateJugement(docId) {
        try {
            await extractDateJugementPortail(token, email, docId);
        } catch { return; }
        for (let i = 0; i < 20; i++) {
            await new Promise((r) => setTimeout(r, 4000));
            try {
                const s = await dateJugementStatutPortail(token, email, docId);
                if (s.state === "done") {
                    setInfo((info) => ({ ...info, documents: (info.documents || []).map((d) => (d.id === docId ? { ...d, remarques: s.remarques } : d)) }));
                    if (s.remarques) toast.success("Date lue automatiquement", { description: s.remarques });
                    return;
                }
                if (s.state === "error" || s.state === "unknown") return;
            } catch { /* réessaie */ }
        }
    }

    async function uploadTyped(files, typeDocument, typePersonnalise = "") {
        for (const file of files) {
            try {
                const doc = await uploadPortailDocument(token, { file, email, typeDocument, typePersonnalise });
                setInfo((i) => ({ ...i, documents: [{ ...doc, type_personnalise: typePersonnalise || doc.type_personnalise }, ...(i.documents || [])] }));
                toast.success("Document transmis", { description: file.name });
            } catch (e) { toast.error(`Échec : ${file.name}`, { description: e.message }); }
        }
    }
    async function prefillIdentite() {
        try { const r = await extractIdentitePortail(token, email); setDirty(true); toast.success(`${r.lignes.length} dirigeant(s) extrait(s)`); return r; }
        catch (e) { toast.error("Extraction impossible", { description: e.message }); return null; }
    }
    async function prefillBanques() {
        try { const r = await extractBanquesPortail(token, email); setDirty(true); toast.success(`${r.lignes.length} compte(s) extrait(s)`); return r; }
        catch (e) { toast.error("Extraction impossible", { description: e.message }); return null; }
    }
    async function prefillCapital() {
        try { const r = await extractCapitalPortail(token, email); setDirty(true); toast.success(`${r.lignes.length} associé(s) extrait(s)`); return r; }
        catch (e) { toast.error("Extraction impossible", { description: e.message }); return null; }
    }
    // SUPER-LOT 84 — Boucle de signature client.
    async function onSignedUpload(doc, file) {
        await uploadPortailSignedDocument(token, doc.id, { file, email });
        const fresh = await fetchPortail(token);
        setInfo(fresh);
        toast.success("Version signée transmise", { description: `${doc.nom_fichier} — merci !` });
    }
    async function onLivrableDownload(doc) {
        try { const url = await portailLivrableDownloadUrl(token, doc.id, email); window.open(url, "_blank", "noopener,noreferrer"); }
        catch (e) { toast.error("Téléchargement impossible", { description: e.message }); }
    }


    if (error) {
        return (
            <div data-testid="portail-page" className="min-h-screen bg-background text-foreground">
                <Header />
                <main className="mx-auto max-w-lg px-6 py-20 text-center space-y-4">
                    <AlertTriangle className="h-8 w-8 mx-auto text-destructive" strokeWidth={1.5} />
                    <h1 className="font-display text-2xl">Lien indisponible</h1>
                    <p data-testid="portail-error" className="text-sm text-muted-foreground">{error}</p>
                </main>
            </div>
        );
    }

    const { done, total } = countReponses(reponses);

    if (submitted) {
        return (
            <div data-testid="portail-page" className="min-h-screen bg-background text-foreground">
                <Header info={info} />
                <main data-testid="portail-thankyou" className="mx-auto max-w-lg px-6 py-24 text-center space-y-5">
                    <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600" strokeWidth={1.5} />
                    <h1 className="font-display text-3xl">Merci !</h1>
                    <p className="text-sm text-muted-foreground">Votre questionnaire a bien été transmis au cabinet. Vous pouvez fermer cette page. Notre équipe reviendra vers vous si un complément d&apos;information est nécessaire.</p>
                    <Button data-testid="portail-thankyou-back" variant="outline" onClick={() => setSubmitted(false)} className="rounded-full">
                        Revenir au questionnaire
                    </Button>
                </main>
            </div>
        );
    }

    return (
        <div data-testid="portail-page" className="min-h-screen bg-background text-foreground">
            <Header info={info} />
            <main className="mx-auto max-w-6xl px-6 py-10 space-y-12">
                {!unlocked ? (
                    <form onSubmit={unlock} data-testid="portail-unlock-form" className="max-w-md space-y-6">
                        <div className="h-11 w-11 rounded-full bg-accent flex items-center justify-center">
                            <Lock className="h-5 w-5 text-primary" strokeWidth={1.75} />
                        </div>
                        <div>
                            <h1 className="font-display text-3xl">Identifiez-vous</h1>
                            <p className="mt-2 text-sm text-muted-foreground">Indiquez votre adresse e-mail pour accéder au questionnaire et au dépôt de documents. Chaque contribution est tracée nominativement pour le cabinet.</p>
                        </div>
                        <Input data-testid="portail-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="prenom.nom@entreprise.fr" className="h-11 bg-card" required />
                        <Button type="submit" data-testid="portail-unlock-button" disabled={!info} className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90 text-primary-foreground">
                            <ShieldCheck className="h-4 w-4" strokeWidth={2} /> Accéder à l&apos;espace
                        </Button>
                    </form>
                ) : (
                    <>
                        <p data-testid="portail-identity" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Connecté en tant que {email}</p>

                        <section data-testid="portail-questionnaire" className="space-y-6">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="h-9 w-9 rounded-full bg-accent flex items-center justify-center">
                                        <ClipboardList className="h-4 w-4 text-primary" strokeWidth={1.75} />
                                    </div>
                                    <div>
                                        <h2 className="font-display text-2xl">Questionnaire dirigeant</h2>
                                        <p className="text-xs text-muted-foreground">Vos réponses sont enregistrées à votre nom · <span data-testid="portail-progress">{done}/{total}</span></p>
                                    </div>
                                </div>
                                <Button data-testid="portail-save-button" onClick={save} disabled={saving || !dirty} className="rounded-full h-10 px-5 bg-primary hover:bg-primary/90 text-primary-foreground">
                                    <Save className="h-4 w-4" strokeWidth={2} /> {saving ? "Enregistrement…" : "Enregistrer mes réponses"}
                                </Button>
                            </div>
                            {info?.contexte_audit && (
                                <p data-testid="portail-contexte-readonly" className="text-xs text-muted-foreground">Mission cadrée par votre conseil : <span className="font-medium text-foreground">{labelContexte(info.contexte_audit)}{info.contexte_autre ? ` — ${info.contexte_autre}` : ""}</span></p>
                            )}
                            <QuestionnaireForm value={reponses} onChange={(v) => { setReponses(v); setDirty(true); }} placeholder="Décrivez ici…" testPrefix="portail-q" onUploadIdentite={(fs) => uploadTyped(fs, "identite_gerant")} identiteDocs={(info?.documents || []).filter((d) => d.type_document === "identite_gerant")} onPrefillIdentite={prefillIdentite} etablissements={etablissements} onUploadReleve={(fs, kind) => uploadTyped(fs, "releve_bancaire", kind === "rib" ? "RIB" : "Relevé bancaire")} releveDocs={(info?.documents || []).filter((d) => d.type_document === "releve_bancaire").map((d) => ({ ...d, _kind: /rib/i.test(`${d.type_personnalise || ""} ${d.nom_fichier || ""}`) ? "rib" : "releve" }))} onPrefillBanques={prefillBanques} onUploadStatuts={(fs) => uploadTyped(fs, "statuts")} statutsDocs={(info?.documents || []).filter((d) => d.type_document === "statuts")} onPrefillCapital={prefillCapital} />
                            <div className="flex flex-wrap justify-end gap-3 border-t border-border pt-4">
                                <Button data-testid="portail-save-button-bottom" variant="outline" onClick={save} disabled={saving || !dirty} className="rounded-full h-10 px-5">
                                    <Save className="h-4 w-4" strokeWidth={2} /> {saving ? "Enregistrement…" : "Enregistrer (brouillon)"}
                                </Button>
                                <Button data-testid="portail-submit-button" onClick={submit} disabled={saving} className="rounded-full h-10 px-5 bg-primary hover:bg-primary/90 text-primary-foreground">
                                    {isCombined ? <ArrowRight className="h-4 w-4" strokeWidth={2} /> : <Send className="h-4 w-4" strokeWidth={2} />}
                                    {saving ? "Envoi…" : isCombined ? "Soumettre et passer au prévisionnel" : "Soumettre le questionnaire"}
                                </Button>
                            </div>
                        </section>

                        <section data-testid="portail-dataroom" className="space-y-6">
                            <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-full bg-accent flex items-center justify-center">
                                    <FileText className="h-4 w-4 text-primary" strokeWidth={1.75} />
                                </div>
                                <div>
                                    <h2 className="font-display text-2xl">Dépôt de documents</h2>
                                    <p className="text-xs text-muted-foreground">Liasses, FEC, relevés… La checklist indique les pièces attendues. Les documents transmis ne peuvent pas être supprimés depuis cet espace.</p>
                                </div>
                            </div>
                            {isRestructuration(info?.contexte_audit) && (
                                <LivrablesSection mode="client" isResto livrables={(info?.documents || []).filter((d) => d.est_livrable)} onUploadSigned={onSignedUpload} onDownload={onLivrableDownload} />
                            )}
                            <PortailDataroom documents={(info?.documents || []).filter((d) => !d.est_livrable)} contexteAudit={info?.contexte_audit} documentsRequis={info?.documents_requis} onUpload={upload} />
                        </section>
                    </>
                )}
            </main>
        </div>
    );
}
